import { useState } from 'react'
import { Play } from 'lucide-react'
import { useProcessDaily } from '../hooks/useProcessDaily'
import type { ProcessDailyResponse } from '../types'

const RESULT_LABELS: { key: keyof Omit<ProcessDailyResponse, 'errors'>; label: string }[] = [
  { key: 'birthdays_processed', label: 'Cumpleanos procesados' },
  { key: 'birthdays_benefits_granted', label: 'Beneficios de cumpleanos otorgados' },
  { key: 'recurring_processed', label: 'Recurrentes procesados' },
  { key: 'recurring_benefits_granted', label: 'Beneficios recurrentes otorgados' },
  { key: 'expired_benefits', label: 'Beneficios expirados' },
  { key: 'expired_vouchers', label: 'Vales expirados' },
  { key: 'expiring_notifications_sent', label: 'Notificaciones de vencimiento enviadas' },
  { key: 'category_changes', label: 'Cambios de categoria' },
  { key: 'category_upgrades', label: 'Subidas de nivel' },
  { key: 'category_downgrades', label: 'Bajadas de nivel' },
  { key: 'benefits_cancelled_downgrade', label: 'Beneficios cancelados (bajada)' },
  { key: 'benefits_cancelled_upgrade', label: 'Beneficios cancelados (subida)' },
]

export function ProcessDailyButton() {
  const { run, isRunning, result, error, reset } = useProcessDaily()
  const [showConfirm, setShowConfirm] = useState(false)

  const handleClick = () => {
    if (result) {
      reset()
      return
    }
    setShowConfirm(true)
  }

  const handleConfirm = async () => {
    setShowConfirm(false)
    await run()
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Ejecuta el proceso diario de beneficios: cumpleanos, cupones recurrentes,
            expiraciones, re-evaluacion de categorias y notificaciones.
          </p>
        </div>
        <button
          onClick={handleClick}
          disabled={isRunning}
          className="btn btn-primary flex items-center gap-2 ml-4 shrink-0"
        >
          {isRunning ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Ejecutando...
            </>
          ) : result ? (
            'Limpiar'
          ) : (
            <>
              <Play size={16} />
              Ejecutar CRON
            </>
          )}
        </button>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowConfirm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900">Ejecutar proceso diario</h3>
            <p className="mt-2 text-sm text-gray-600">
              Esto ejecutara el CRON de beneficios. Se procesaran cumpleanos, cupones recurrentes,
              expiraciones y re-evaluacion de categorias. Es seguro ejecutar multiples veces
              (idempotente).
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowConfirm(false)} className="btn btn-secondary">
                Cancelar
              </button>
              <button onClick={handleConfirm} className="btn btn-primary">
                Ejecutar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm font-medium text-green-800 mb-3">Proceso completado</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            {RESULT_LABELS.map(({ key, label }) => {
              const value = result[key]
              if (value === 0) return null
              return (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-gray-700">{label}</span>
                  <span className="font-medium text-gray-900">{value}</span>
                </div>
              )
            })}
            {RESULT_LABELS.every(({ key }) => result[key] === 0) && (
              <p className="text-sm text-gray-600 col-span-2">Sin cambios. Todo al dia.</p>
            )}
          </div>
          {result.errors.length > 0 && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <p className="font-medium">Errores ({result.errors.length}):</p>
              <ul className="list-disc pl-4 mt-1">
                {result.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
